"use client";

// Мелочи прокрутки, общие для всех длинных списков и таблиц портала.
//
// №13 — затухание у края. Последняя строка не обрезается ножом, а растворяется:
//       сразу видно, что список продолжается. Затухание само гаснет, когда
//       докрутили до конца, — иначе оно врёт, обещая продолжение, которого нет.
// №37 — «Наверх». Кнопка не занимает место постоянно: всплывает после третьего
//       экрана и прячется, когда вернулись. В таблице на восемьсот строк это
//       единственный быстрый способ добраться до шапки без клавиши Home.

import { useCallback, useEffect, useRef, useState } from "react";
import { IconChevronUp } from "@/components/ui/icons";

/** Ставит на элемент data-fade="none|top|bottom|both" — CSS по нему рисует маску. */
export function useEdgeFade<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 4 px — допуск: дробная высота строк на масштабе браузера не даёт
    // scrollTop дойти до scrollHeight точно, и затухание оставалось бы навсегда.
    const top = el.scrollTop > 4;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    el.dataset.fade = top && bottom ? "both" : top ? "top" : bottom ? "bottom" : "none";
  }, []);

  const setRef = useCallback((el: T | null) => {
    ref.current = el;
    if (el) update();
  }, [update]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, [update]);

  return { ref, setRef, update };
}

/** Кнопка «Наверх» для КОНКРЕТНОГО прокручиваемого блока (не для окна). */
export function BackToTop({
  targetRef,
  /** После скольких экранов показывать. Три — из практики длинных таблиц. */
  screens = 3,
}: {
  targetRef: { current: HTMLElement | null };
  screens?: number;
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const onScroll = () => setShow(el.scrollTop > el.clientHeight * screens);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [targetRef, screens]);

  return (
    <button
      type="button"
      aria-hidden={!show}
      tabIndex={show ? 0 : -1}
      onClick={() => targetRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
      className={`back-to-top glass-hover absolute bottom-3 right-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--textMuted)]${show ? " is-on" : ""}`}
      style={{ boxShadow: "0 8px 20px -8px rgba(0,0,0,.5)" }}
      data-tooltip="Наверх"
    >
      <IconChevronUp size={15} />
    </button>
  );
}
